/**
 * Storefront API client.
 *
 * Every route from docs/API_CONTRACT.md "Storefront routes" is a typed method on
 * the `Api` interface. Two adapters implement it:
 *  - realApi:  fetch against `import.meta.env.VITE_API_BASE` (default
 *              '/apps/print/api', the Shopify app-proxy path).
 *  - mockApi:  a full in-memory backend (VITE_MOCK=1) with fake latency and fake
 *              presigned uploads, so `npm run dev` runs standalone.
 *
 * The active adapter is chosen once from the env and exported as `api`.
 */

import {
  computeTotals,
  framedPriceFor,
  priceFor,
  seedPricing,
  type PricingData,
  type PrintSelection,
  type Totals,
} from '@rpl/pricing';
import type {
  AcceptShareResponse,
  CheckoutResponse,
  CreatePhotoRequest,
  CreatePhotoResponse,
  CreateProjectResponse,
  MintShareResponse,
  MpuCompleteRequest,
  MpuPartResponse,
  OrderStatusResponse,
  PatchProjectRequest,
  PatchProjectResponse,
  ProjectState,
  SaveProjectResponse,
  WirePhoto,
  WirePrint,
} from './types';
import { formatBytes } from './format';
import { gbCapLabel } from './uploadLimits';
import { delay, randomHex, uid } from './util';

const DEFAULT_BASE = '/apps/print/api';
const MPU_THRESHOLD = 104857600; // 100 MiB — matches worker
const MPU_PART_SIZE = 52428800; // 50 MiB

export class ApiError extends Error {
  constructor(
    /** machine code, e.g. 'project_not_found', 'file_too_large'. */
    readonly code: string,
    message: string,
    readonly httpStatus: number,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface Api {
  readonly isMock: boolean;
  readonly base: string;
  getPricing(): Promise<{ version: number; data: PricingData }>;
  createProject(): Promise<CreateProjectResponse>;
  getProject(projectId: string, token: string): Promise<ProjectState>;
  patchProject(
    projectId: string,
    token: string,
    body: PatchProjectRequest,
  ): Promise<PatchProjectResponse>;
  createPhoto(
    projectId: string,
    token: string,
    body: CreatePhotoRequest,
  ): Promise<CreatePhotoResponse>;
  getMpuPart(
    projectId: string,
    token: string,
    photoId: string,
    n: number,
    uploadId: string,
  ): Promise<MpuPartResponse>;
  completeMpu(
    projectId: string,
    token: string,
    photoId: string,
    body: MpuCompleteRequest,
  ): Promise<{ ok: true }>;
  confirmUploaded(projectId: string, token: string, photoId: string): Promise<{ ok: true }>;
  deletePhoto(projectId: string, token: string, photoId: string): Promise<{ ok: true }>;
  /** URL for an <img> src — token passed via ?pt= fallback per the contract. */
  previewUrl(projectId: string, photoId: string, token: string): string;
  saveProject(projectId: string, token: string, email: string): Promise<SaveProjectResponse>;
  /** POST /checkout — optional body carries the urgent-processing toggle. */
  checkout(projectId: string, token: string, body?: { urgent?: boolean }): Promise<CheckoutResponse>;
  getOrder(orderName: string, statusToken: string): Promise<OrderStatusResponse>;
  /** Mint (or reuse) a share link for an order — gated by the status token. */
  mintShare(orderName: string, statusToken: string): Promise<MintShareResponse>;
  /** Accept a share ("<id>.<token>") into a brand-new project with cloned photos. */
  acceptShare(share: string): Promise<AcceptShareResponse>;
}

/* ============================================================
   Real adapter
   ============================================================ */

/**
 * The status contract is `summary: [{label, qty, detail}]`, but early orders
 * (pre 2026-07-10 intake fix) stored the human string "8×8 Gloss ×1; …" in D1.
 * Normalize both so the status page can never crash on the field shape.
 */
function normalizeSummary(s: unknown): OrderStatusResponse['summary'] {
  if (Array.isArray(s)) return s as OrderStatusResponse['summary'];
  if (typeof s === 'string' && s.trim()) {
    return s.split(/;\s*/).map((part) => {
      const m = /^(.*?)\s*[×x]\s*(\d+)$/.exec(part.trim());
      return m
        ? { label: m[1], qty: Number(m[2]), detail: '' }
        : { label: part.trim(), qty: 1, detail: '' };
    });
  }
  return [];
}

function makeRealApi(base: string): Api {
  async function req<T>(
    path: string,
    opts: { method?: string; body?: unknown; token?: string } = {},
  ): Promise<T> {
    const headers: Record<string, string> = {};
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    if (opts.token) headers['X-RPL-Project-Token'] = opts.token;
    const res = await fetch(base + path, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
    if (!res.ok) {
      let code = `http_${res.status}`;
      let message = res.statusText;
      let detail: unknown;
      try {
        const j = (await res.json()) as { error?: string; message?: string; detail?: unknown };
        if (j.error) code = j.error;
        if (j.message) message = j.message;
        detail = j.detail;
      } catch {
        /* non-JSON error body */
      }
      throw new ApiError(code, message, res.status, detail);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  return {
    isMock: false,
    base,
    getPricing: () => req('/pricing'),
    createProject: () => req('/projects', { method: 'POST' }),
    getProject: (id, token) => req(`/projects/${id}`, { token }),
    patchProject: (id, token, body) =>
      req(`/projects/${id}`, { method: 'PATCH', token, body }),
    createPhoto: (id, token, body) =>
      req(`/projects/${id}/photos`, { method: 'POST', token, body }),
    getMpuPart: (id, token, pid, n, uploadId) =>
      req(
        `/projects/${id}/photos/${pid}/mpu-part?n=${n}&uploadId=${encodeURIComponent(uploadId)}`,
        { token },
      ),
    completeMpu: (id, token, pid, body) =>
      req(`/projects/${id}/photos/${pid}/mpu-complete`, { method: 'POST', token, body }),
    confirmUploaded: (id, token, pid) =>
      req(`/projects/${id}/photos/${pid}/uploaded`, { method: 'POST', token }),
    deletePhoto: (id, token, pid) =>
      req(`/projects/${id}/photos/${pid}`, { method: 'DELETE', token }),
    previewUrl: (id, pid, token) =>
      `${base}/projects/${id}/previews/${pid}?pt=${encodeURIComponent(token)}`,
    saveProject: (id, token, email) =>
      req(`/projects/${id}/save`, { method: 'POST', token, body: { email } }),
    checkout: (id, token, body) => req(`/projects/${id}/checkout`, { method: 'POST', token, body }),
    getOrder: async (orderName, statusToken) => {
      const res = await req<OrderStatusResponse>(
        `/orders/${encodeURIComponent(orderName)}?t=${encodeURIComponent(statusToken)}`,
      );
      return { ...res, summary: normalizeSummary(res.summary) };
    },
    mintShare: (orderName, statusToken) =>
      req(`/orders/${encodeURIComponent(orderName)}/share?t=${encodeURIComponent(statusToken)}`, {
        method: 'POST',
      }),
    acceptShare: (share) => req('/shares/accept', { method: 'POST', body: { share } }),
  };
}

/* ============================================================
   Mock adapter — full in-memory backend
   ============================================================ */

interface MockProject {
  project_id: string;
  token: string;
  status: string;
  email: string | null;
  ui_state: ProjectState['ui_state'];
  photos: WirePhoto[];
  prints: WirePrint[];
  created_at: string;
  updated_at: string;
  /** photo_id -> pending upload bookkeeping (fake). */
  uploads: Map<string, { uploaded: boolean }>;
}

function stableVariantId(sku: string): number {
  // Deterministic pseudo-variant id so the mock cart looks realistic.
  let h = 0;
  for (let i = 0; i < sku.length; i++) h = (h * 31 + sku.charCodeAt(i)) >>> 0;
  return 40000000000 + (h % 9999999);
}

function makeMockApi(): Api {
  const data = seedPricing;
  const projects = new Map<string, MockProject>();
  /** orderName -> minted share url (re-click returns the SAME link, like the worker). */
  const mintedShares = new Map<string, { share_url: string; expires_at: string }>();
  const lat = () => delay(120 + Math.random() * 260); // fake network latency

  function need(id: string, token: string): MockProject {
    const p = projects.get(id);
    if (!p || p.token !== token) {
      throw new ApiError('project_not_found', 'Project not found or expired.', 404);
    }
    return p;
  }

  function toSelections(prints: WirePrint[]): PrintSelection[] {
    return prints.map((p) => ({
      sizeId: p.size_id,
      paper: p.paper,
      qty: p.qty,
      ...(p.frame_id ? { frameId: p.frame_id } : {}),
    }));
  }

  function totalsFor(prints: WirePrint[]): Totals {
    return computeTotals(data, toSelections(prints));
  }

  return {
    isMock: true,
    base: 'mock://',

    async getPricing() {
      await lat();
      return { version: data.version, data };
    },

    async createProject() {
      await lat();
      const now = new Date().toISOString();
      const project_id = `p_${randomHex(10)}`;
      const token = randomHex(24);
      projects.set(project_id, {
        project_id,
        token,
        status: 'draft',
        email: null,
        ui_state: null,
        photos: [],
        prints: [],
        created_at: now,
        updated_at: now,
        uploads: new Map(),
      });
      return { project_id, token };
    },

    async getProject(id, token) {
      await lat();
      const p = need(id, token);
      return {
        project_id: p.project_id,
        status: p.status,
        email: p.email,
        ui_state: p.ui_state,
        photos: p.photos,
        prints: p.prints,
        created_at: p.created_at,
        updated_at: p.updated_at,
      };
    },

    async patchProject(id, token, body) {
      await lat();
      const p = need(id, token);
      if (body.ui_state !== undefined) p.ui_state = body.ui_state;
      if (body.prints !== undefined) {
        p.prints = body.prints.map((u) => {
          const unit = u.frame_id
            ? framedPriceFor(data, u.frame_id)
            : priceFor(data, u.size_id, u.paper);
          return {
            print_id: u.print_id ?? `pr_${randomHex(8)}`,
            photo_id: u.photo_id,
            size_id: u.size_id,
            paper: u.paper,
            qty: Math.max(1, Math.min(999, u.qty)),
            fit_mode: u.fit_mode ?? 'crop',
            crop_rect: u.crop_rect ?? null,
            print_check: u.print_check,
            override_lowres: u.override_lowres ?? false,
            effective_dpi: u.effective_dpi ?? 0,
            unit_price: unit,
            rotation: u.rotation ?? 0,
            bw: u.bw ?? false,
            ...(u.frame_id ? { frame_id: u.frame_id } : {}),
          };
        });
      }
      p.updated_at = new Date().toISOString();
      return { prints: p.prints, totals: totalsFor(p.prints) };
    },

    async createPhoto(id, token, reqBody) {
      await lat();
      const p = need(id, token);
      if (p.photos.length >= data.limits.maxPhotosPerOrder) {
        throw new ApiError('too_many_photos', 'That is over the photo limit for one order.', 422);
      }
      if (reqBody.bytes > data.limits.maxFileBytes) {
        // Size comes from the limit itself: a hardcoded number here said 500 MB
        // for as long as it took someone to notice the cap had moved.
        throw new ApiError(
          'file_too_large',
          `That file is over ${formatBytes(data.limits.maxFileBytes)}.`,
          422,
        );
      }
      // Order cap (limits.maxOrderBytes) — mirrors the worker's gate. The label
      // is derived, not hardcoded: the cap moved 5 GB -> 10 GB in 2026-08.
      const orderBytes = p.photos.reduce((n, ph) => n + ph.bytes, 0);
      if (orderBytes + reqBody.bytes > data.limits.maxOrderBytes) {
        throw new ApiError(
          'order_too_large',
          `This order is over the ${gbCapLabel(data.limits.maxOrderBytes)} upload limit.`,
          422,
        );
      }
      const photo_id = `ph_${randomHex(10)}`;
      p.photos.push({
        photo_id,
        filename: reqBody.filename,
        width: reqBody.width,
        height: reqBody.height,
        bytes: reqBody.bytes,
        sha256: reqBody.sha256,
        upload_status: 'pending',
        sort_order: p.photos.length,
      });
      p.uploads.set(photo_id, { uploaded: false });
      const original =
        reqBody.bytes >= MPU_THRESHOLD
          ? ({
              mode: 'mpu' as const,
              key: `projects/${id}/original/${photo_id}/${reqBody.filename}`,
              upload_id: `mpu_${randomHex(8)}`,
              part_size: MPU_PART_SIZE,
            })
          : ({ mode: 'single' as const, put_url: `mock://original/${photo_id}` });
      return {
        photo_id,
        preview_put_url: `mock://preview/${photo_id}`,
        original,
      };
    },

    async getMpuPart(id, token, photoId, n) {
      await lat();
      need(id, token);
      return { put_url: `mock://original/${photoId}/part/${n}` };
    },

    async completeMpu(id, token) {
      await lat();
      need(id, token);
      return { ok: true };
    },

    async confirmUploaded(id, token, photoId) {
      await lat();
      const p = need(id, token);
      const photo = p.photos.find((x) => x.photo_id === photoId);
      if (!photo) throw new ApiError('not_an_image', 'Unknown photo.', 422);
      photo.upload_status = 'uploaded';
      p.uploads.set(photoId, { uploaded: true });
      return { ok: true };
    },

    async deletePhoto(id, token, photoId) {
      await lat();
      const p = need(id, token);
      p.photos = p.photos.filter((x) => x.photo_id !== photoId);
      p.prints = p.prints.filter((x) => x.photo_id !== photoId);
      p.uploads.delete(photoId);
      return { ok: true };
    },

    previewUrl(id, photoId) {
      // In mock mode previews are local object URLs; this is only hit for
      // server-hydrated photos (rare in standalone dev). Return a transparent px.
      void id;
      void photoId;
      return 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    },

    async saveProject(id, token, email) {
      await lat();
      const p = need(id, token);
      p.email = email;
      p.status = 'saved';
      return {
        ok: true,
        resume_url: `https://www.rewindphotolab.com.au/apps/print?resume=${id}.${token}`,
      };
    },

    async checkout(id, token, body) {
      await lat();
      void body; // the mock has no fee variant — the worker appends the fee line in real mode
      const p = need(id, token);
      if (p.prints.length === 0) throw new ApiError('empty_project', 'No prints yet.', 422);
      const totals = totalsFor(p.prints);
      if (totals.groups.length > data.limits.maxCartGroups) {
        throw new ApiError('too_many_groups', 'Too many distinct lines.', 422);
      }
      const photoCountBySize = new Map<string, Set<string>>();
      for (const pr of p.prints) {
        const key = pr.frame_id ?? `${pr.size_id}|${pr.paper}`;
        if (!photoCountBySize.has(key)) photoCountBySize.set(key, new Set());
        photoCountBySize.get(key)!.add(pr.photo_id);
      }
      const items = totals.groups.map((g) => {
        const key = g.frameId ?? `${g.sizeId}|${g.paper}`;
        const photos = photoCountBySize.get(key)?.size ?? 0;
        return {
          id: stableVariantId(g.sku),
          quantity: g.qty,
          properties: {
            ...g.displayProps,
            Details: `${g.qty} prints · ${photos} photos`,
            _rpl: '1',
            _project_id: id,
            _group: g.frameId ?? `${g.sizeId}|${g.paper}`,
          },
        };
      });
      p.status = 'submitted';
      return { cart: { items }, totals };
    },

    async getOrder(orderName, statusToken) {
      await lat();
      void statusToken;
      const now = new Date().toISOString();
      return {
        order_name: orderName,
        status: 'received',
        timeline: [{ status: 'received', at: now }],
        fulfilment: 'pickup_sydney',
        eta: null,
        tracking_url: null,
        customer_first: 'Friend',
        summary: [],
      };
    },

    async mintShare(orderName, statusToken) {
      await lat();
      void statusToken;
      const existing = mintedShares.get(orderName);
      if (existing) return existing; // re-click -> same link
      const minted = {
        share_url: `https://www.rewindphotolab.com.au/apps/print?share=sh_${randomHex(10)}.${randomHex(32)}`,
        expires_at: new Date(Date.now() + 30 * 86400000).toISOString(),
      };
      mintedShares.set(orderName, minted);
      return minted;
    },

    async acceptShare(share) {
      await lat();
      const dot = share.indexOf('.');
      if (dot <= 0 || dot === share.length - 1) {
        throw new ApiError('share_not_found', 'That share link is not valid.', 404);
      }
      // Demo hooks: sh_expired… simulates 410, sh_missing… simulates 404.
      if (share.startsWith('sh_expired')) {
        throw new ApiError('share_expired', 'This share link has expired.', 410);
      }
      if (share.startsWith('sh_missing')) {
        throw new ApiError('share_not_found', 'That share link is not valid.', 404);
      }
      const now = new Date().toISOString();
      const project_id = `p_${randomHex(10)}`;
      const token = randomHex(24);
      const photos: WirePhoto[] = [0, 1, 2].map((i) => ({
        photo_id: `ph_${randomHex(10)}`,
        filename: `shared-photo-${i + 1}.jpg`,
        width: 3000,
        height: 2000,
        bytes: 2_400_000,
        sha256: randomHex(32),
        upload_status: 'uploaded',
        sort_order: i,
      }));
      projects.set(project_id, {
        project_id,
        token,
        status: 'draft',
        email: null,
        ui_state: null,
        photos,
        prints: [],
        created_at: now,
        updated_at: now,
        uploads: new Map(photos.map((p) => [p.photo_id, { uploaded: true }])),
      });
      return { project_id, token, photo_count: photos.length };
    },
  };
}

/* ============================================================
   Factory + singleton
   ============================================================ */

export function createApi(): Api {
  // Mock when VITE_MOCK=1, or by default in dev (so `npm run dev` runs
  // standalone). Set VITE_MOCK=0 in dev to hit a real worker.
  const explicit = import.meta.env.VITE_MOCK;
  const isMock = explicit === '1' || (import.meta.env.DEV && explicit !== '0');
  if (isMock) return makeMockApi();
  const base = import.meta.env.VITE_API_BASE ?? DEFAULT_BASE;
  return makeRealApi(base);
}

export const api: Api = createApi();
